// One piece of text in the live preview that the reviewer edits in place.
// Plain text only: the template owns fonts and styling, so there is no
// bold/italic — but list structure behaves like Word (Enter, Tab,
// Backspace at the start, paste of bulleted text), through `lineOps`.
//
// The element is uncontrolled: React never re-renders its text while the
// reviewer types (that would move the caret); the text is only pushed in
// when the value changes from outside (the form on the left, undo).

import { createContext, useContext, useLayoutEffect, useRef } from "react";
import type { CSSProperties, KeyboardEvent, ClipboardEvent, FormEvent } from "react";
import type { PastedLine } from "@agp/shared";
import { parsePastedText } from "@agp/shared";
import { caretOffset, caretOnEdgeLine, setCaret } from "@/lib/caret";
import { parsePastedHtml, pastedPlainLine } from "@/lib/paste";

export interface FocusRequest {
  id: string;
  offset: number;
}

export interface LineOps {
  onSplit(offset: number): void;
  onBackspaceAtStart(): void;
  onDeleteAtEnd(): void;
  onIndent(offset: number): void;
  onOutdent(offset: number): void;
  onPasteLines(offset: number, pasted: PastedLine[]): void;
  /** ArrowUp on the first line / ArrowDown on the last: move to the
   *  neighbouring line, keeping the column when the neighbour is long
   *  enough. Left at the start and Right at the end do the same with
   *  offset 0 / Infinity. */
  onArrow(direction: "prev" | "next", offset: number): void;
}

export interface EditSession {
  /** Where the caret should land after the next render. */
  requestFocus(req: FocusRequest): void;
  /** The reviewer clicked into a field: remember it for undo/redo. */
  noteFocus(req: FocusRequest): void;
  /** Move the caret to the editable before/after this one in the document. */
  focusRelative(fromId: string, direction: "prev" | "next", offset: number): void;
}

export const EditSessionContext = createContext<EditSession | null>(null);

export function Editable({
  id,
  value,
  onCommit,
  lineOps,
  block = true,
  style,
  className,
  placeholder,
  readOnly = false,
  multiline = false,
  onEmptyBackspace,
}: {
  id: string;
  value: string;
  /** Text after the edit, and where the caret is in it. */
  onCommit: (text: string, caret: number) => void;
  lineOps?: LineOps;
  /** Fill the line (div) or sit inline (span). */
  block?: boolean;
  style?: CSSProperties;
  className?: string;
  placeholder?: string;
  /** The measuring copy of the preview and non-editing previews render the
   *  same element without editing, so line heights match exactly. */
  readOnly?: boolean;
  /** Enter inserts a line break instead of leaving the field (a paragraph
   *  of an appended Word original). */
  multiline?: boolean;
  /** Backspace on an empty field asks the owner to drop it. */
  onEmptyBackspace?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const session = useContext(EditSessionContext);
  const editing = !readOnly && session != null;

  // While the reviewer is typing here the DOM is the truth: a composite
  // line ("Master's, Marketing — Honours") is stored as fields and comes
  // back normalised, which would eat the separator being typed. The value
  // is pushed in when it changes from elsewhere and when the field is left.
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && el.textContent !== value && document.activeElement !== el) el.textContent = value;
  }, [value]);

  const onBlur = () => {
    const el = ref.current;
    if (el && el.textContent !== value) el.textContent = value;
  };

  const onInput = (e: FormEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    // The browser may leave a <br> behind after deleting everything.
    if (el.querySelector("br, div")) {
      const text = el.textContent ?? "";
      el.textContent = text;
      setCaret(el, text.length);
    }
    // Chrome shows the caret on a new last line by doubling the newline;
    // the stored text keeps just one.
    const text = (el.textContent ?? "").replace(/\n\n$/, "\n");
    onCommit(text, caretOffset(el) ?? text.length);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const mod = e.metaKey || e.ctrlKey;
    if (mod && /^[biu]$/i.test(e.key)) {
      e.preventDefault();
      return;
    }
    if (mod) return;
    const offset = caretOffset(el);
    const len = (el.textContent ?? "").length;
    if (e.key === "Enter") {
      // Multiline fields let the browser insert the newline itself: in
      // plaintext-only editing Chrome also keeps the caret on the new line,
      // which a hand-inserted "\n" at the end of the text cannot do.
      if (multiline && !lineOps) return;
      e.preventDefault();
      if (lineOps) lineOps.onSplit(offset ?? len);
      else el.blur();
      return;
    }
    if (e.key === "Backspace" && len === 0 && onEmptyBackspace) {
      e.preventDefault();
      onEmptyBackspace();
      return;
    }
    if (!lineOps) {
      if (e.key === "ArrowUp" && caretOnEdgeLine(el, "first")) {
        e.preventDefault();
        session?.focusRelative(id, "prev", offset ?? 0);
      } else if (e.key === "ArrowDown" && caretOnEdgeLine(el, "last")) {
        e.preventDefault();
        session?.focusRelative(id, "next", offset ?? len);
      }
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      (e.shiftKey ? lineOps.onOutdent : lineOps.onIndent)(offset ?? 0);
      return;
    }
    if (e.key === "Backspace" && offset === 0) {
      e.preventDefault();
      lineOps.onBackspaceAtStart();
      return;
    }
    if (e.key === "Delete" && offset === len) {
      e.preventDefault();
      lineOps.onDeleteAtEnd();
      return;
    }
    if (e.key === "ArrowUp" && caretOnEdgeLine(el, "first")) {
      e.preventDefault();
      lineOps.onArrow("prev", offset ?? 0);
      return;
    }
    if (e.key === "ArrowDown" && caretOnEdgeLine(el, "last")) {
      e.preventDefault();
      lineOps.onArrow("next", offset ?? len);
      return;
    }
    if (e.key === "ArrowLeft" && offset === 0) {
      e.preventDefault();
      lineOps.onArrow("prev", Infinity);
      return;
    }
    if (e.key === "ArrowRight" && offset === len) {
      e.preventDefault();
      lineOps.onArrow("next", 0);
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const el = e.currentTarget;
    const html = e.clipboardData.getData("text/html") || null;
    const text = e.clipboardData.getData("text/plain");
    const offset = caretOffset(el) ?? (el.textContent ?? "").length;
    if (lineOps) {
      lineOps.onPasteLines(offset, html ? parsePastedHtml(html) : parsePastedText(text));
      return;
    }
    const line = pastedPlainLine(html, text);
    const current = el.textContent ?? "";
    const sel = window.getSelection();
    const selected = sel && !sel.isCollapsed && el.contains(sel.anchorNode) ? sel.toString().length : 0;
    const next = current.slice(0, offset) + line + current.slice(offset + selected);
    el.textContent = next;
    setCaret(el, offset + line.length);
    onCommit(next, offset + line.length);
  };

  const Tag = block ? "div" : "span";
  return (
    <Tag
      ref={ref as never}
      data-edit-id={editing ? id : undefined}
      contentEditable={editing ? "plaintext-only" : undefined}
      suppressContentEditableWarning
      spellCheck={false}
      className={className}
      data-placeholder={editing && placeholder ? placeholder : undefined}
      data-empty={value === "" ? "" : undefined}
      style={{
        ...(block ? { display: "block", minHeight: "1.35em" } : { display: "inline-block", minWidth: "1ch" }),
        outline: "none",
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        ...style,
      }}
      onInput={editing ? onInput : undefined}
      onKeyDown={editing ? onKeyDown : undefined}
      onPaste={editing ? onPaste : undefined}
      onDrop={editing ? (e) => e.preventDefault() : undefined}
      onBlur={editing ? onBlur : undefined}
      onFocus={editing ? (e) => session?.noteFocus({ id, offset: caretOffset(e.currentTarget) ?? 0 }) : undefined}
    />
  );
}
